import SelectBuscable from '@/shared/components/SelectBuscable'
import { TituloDePantalla } from '@/shared/components/TituloDePantalla'
import React, { useState, useEffect, useMemo } from "react";
import {
  Search,
  PlusCircle,
  Loader2,
  CheckCircle,
  FileUp,
  Download,
} from "lucide-react";
import { adminPurchaseOrdersService } from '@/features/proveedores/api/adminPurchaseOrders.service';
import { purchaseOrdersRHService } from '@/features/rh/api/purchaseOrdersRH.service';
import { AddPaymentModal, type PaymentFormData } from '@/features/rh/components/rh/AddPaymentModal';
import emptyInvoice from '@/assets/empty-invoice.png';
import { EsqueletoTabla } from "@/shared/components/Esqueleto";
import { Badge } from "@/shared/components/Badge";
import { estiloDeBoton } from "@/shared/lib/estilosDeBoton";

const CalendarFolded = ({
  className,
  ...props
}: React.SVGProps<SVGSVGElement>) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    {...props}
  >
    <path d="M16 3H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12l5-5V5a2 2 0 0 0-2-2Z" />
    <path d="M21 14h-5a2 2 0 0 0-2 2v5" />
    <path d="M8 2v4" />
    <path d="M16 2v4" />
    <path d="M2 9h19" />
  </svg>
);

// El input type=date siempre trae el value en formato ISO (yyyy-mm-dd) sin
// importar el locale; esto solo lo reformatea para mostrarlo como dd/mm/aaaa,
// que es el formato que pide el diseño (independiente del locale del navegador).
const formatDateDDMMYYYY = (isoDate: string) => {
  const [year, month, day] = isoDate.split("-");
  if (!year || !month || !day) return "";
  return `${day}/${month}/${year}`;
};

// Fecha LOCAL de un valor de fecha del backend en formato YYYY-MM-DD. Usa la
// misma zona horaria que la columna visible (que se formatea con
// toLocaleDateString), para que el filtro coincida con la fecha que ve el
// usuario y no se corra un día por diferencia UTC/local.
const toLocalYMD = (value?: string | null) => {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

export const GlobalPaymentsView: React.FC = () => {
  const [invoices, setInvoices] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [supplierFilter, setSupplierFilter] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [preselectedInvoiceId, setPreselectedInvoiceId] = useState<
    string | undefined
  >(undefined);
  const [success, setSuccess] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadMsg, setDownloadMsg] = useState("");

  // Abre el modal de pago con una factura ya seleccionada (botón por fila).
  const handleRegisterPago = (invoiceId: string) => {
    setPreselectedInvoiceId(invoiceId);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setPreselectedInvoiceId(undefined);
  };

  useEffect(() => {
    fetchGlobalInvoices();
  }, []);

  const fetchGlobalInvoices = async () => {
    setLoading(true);
    try {
      const data = await adminPurchaseOrdersService.getAllInvoices();
      const invoicesArray = Array.isArray(data)
        ? data
        : ((data as { data?: unknown[] })?.data ?? []);
      setInvoices(invoicesArray);
    } catch (error) {
      console.error("Error al cargar facturas:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddPayment = async (data: PaymentFormData) => {
    const ids = data.invoiceIds?.length ? data.invoiceIds : (data.invoiceId ? [data.invoiceId] : []);
    if (ids.length === 0) throw new Error("Agrega al menos una factura");

    // El endpoint no cuelga de una orden: el pago puede cubrir facturas de
    // varias ordenes del mismo proveedor, y el reparto entre ellas lo hace el
    // back con el saldo de cada una.
    await purchaseOrdersRHService.createBatchPayment({
      invoiceIds: ids,
      amount: data.amount,
      payment_date: data.payment_date,
      payment_method: data.payment_method,
      reference_number: data.reference_number,
      notes: data.notes,
      receipt: data.receipt,
    });

    setSuccess(true);
    setTimeout(() => setSuccess(false), 3000);
    await fetchGlobalInvoices();
  };

  const handleClearFilters = () => {
    setSearchTerm("");
    setStatusFilter("");
    setSupplierFilter("");
    setFromDate("");
    setToDate("");
  };

  const suppliers = useMemo(() => {
    const names = invoices.map((inv) => inv.supplierName).filter(Boolean);
    return [...new Set(names)] as string[];
  }, [invoices]);

  // Expandir facturas: una fila por pago, o una fila si no tiene pagos
  const rows = useMemo(() => {
    const expanded: any[] = [];
    invoices.forEach((inv) => {
      if (inv.payments && inv.payments.length > 0) {
        inv.payments.forEach((payment: any) => {
          expanded.push({ ...inv, _payment: payment });
        });
      } else {
        expanded.push({ ...inv, _payment: null });
      }
    });
    return expanded;
  }, [invoices]);

  const filteredRows = useMemo(
    () =>
      rows.filter((row) => {
        const matchesSearch =
          row.poNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          row.supplierName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          row.xml_file_name?.toLowerCase().includes(searchTerm.toLowerCase());

        // Solo facturas aprobadas (con o sin pago registrado).
        const isApprovedInvoice =
          row.status === "approved" || row.status === "paid";

        // Filtro por estado de pago: "" todas | "pendiente" (sin pago) | "pagada" (con pago).
        const isPaid = Boolean(row._payment);
        const matchesEstado =
          !statusFilter || (statusFilter === "pagada" ? isPaid : !isPaid);

        const matchesSupplier =
          !supplierFilter || row.supplierName === supplierFilter;

        // Filtro por fecha de aprobación. Se usa la fecha LOCAL (misma que
        // muestra la columna) en formato YYYY-MM-DD para comparar contra
        // fromDate/toDate del input; rango inclusivo y coincide con lo que ve
        // el usuario.
        const createdDate = toLocalYMD(row.created_at);
        const matchesFrom =
          !fromDate || (createdDate !== "" && createdDate >= fromDate);
        const matchesTo =
          !toDate || (createdDate !== "" && createdDate <= toDate);

        return (
          matchesSearch &&
          isApprovedInvoice &&
          matchesEstado &&
          matchesSupplier &&
          matchesFrom &&
          matchesTo
        );
      }),
    [rows, searchTerm, statusFilter, supplierFilter, fromDate, toDate],
  );

  // Solo facturas aprobadas para el modal
  const approvedInvoices = useMemo(
    () => invoices.filter((inv) => inv.status === "approved"),
    [invoices],
  );

  // Descarga el Excel respetando los filtros activos. Si no hay filas visibles,
  // no descarga y muestra el mensaje de sin datos.
  const handleDownloadExcel = async () => {
    if (filteredRows.length === 0) {
      setDownloadMsg("No hay datos para descargar. Intenta con otros filtros.");
      setTimeout(() => setDownloadMsg(""), 3500);
      return;
    }
    try {
      setDownloading(true);
      const blob = await adminPurchaseOrdersService.exportPagos({
        estado: statusFilter,
        proveedor: supplierFilter,
        fechaDesde: fromDate,
        fechaHasta: toDate,
        search: searchTerm,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const fecha = new Date().toISOString().slice(0, 10);
      a.download = `pagos_SLYRSA_${fecha}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      setDownloadMsg("No se pudo generar el Excel. Intenta de nuevo.");
      setTimeout(() => setDownloadMsg(""), 3500);
    } finally {
      setDownloading(false);
    }
  };

  const getPaymentStatusMeta = (hasPayment: boolean) =>
    hasPayment
      ? { label: "Pagada", color: "verde" as const }
      : { label: "Pendiente de pago", color: "amarillo" as const };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      {/* Header */}
      <div className="flex justify-between items-start mb-6">
        <div>
          <TituloDePantalla className="mb-2">
            Facturas y pagos
          </TituloDePantalla>
          <p className="text-gray-600 dark:text-gray-400">
            Consulta las facturas aprobadas y registra sus pagos.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleDownloadExcel}
            disabled={downloading}
            className={estiloDeBoton("secundario")}
          >
            {downloading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Generando…
              </>
            ) : (
              <>
                <Download className="w-4 h-4" />
                Descargar Excel
              </>
            )}
          </button>
          <button
            onClick={() => {
              setPreselectedInvoiceId(undefined);
              setIsModalOpen(true);
            }}
            className={estiloDeBoton("primario")}
          >
            <PlusCircle className="w-4 h-4" />
            Agregar pago
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-4 py-3 flex flex-wrap justify-start items-center gap-3 mb-6">
        <div className="relative w-[280px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar en pagos..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-3 py-2 !border !border-[#E5E7EB] rounded-lg text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#3B5998] focus:border-transparent"
          />
        </div>
        <div className="relative">
          <SelectBuscable
            variante="listado"
            ancho="contenido"
            etiqueta="Estado"
            todos="Todos"
            value={statusFilter}
            onChange={setStatusFilter}
            placeholderDeBusqueda="Buscar estado…"
            canDelete={false}
            opciones={[
              { value: "pendiente", label: "Pendiente de pago" },
              { value: "pagada", label: "Pagada" },
            ]}
          />
        </div>
        <div className="relative">
          <SelectBuscable
            variante="listado"
            ancho="contenido"
            etiqueta="Proveedor"
            todos="Todos"
            value={supplierFilter}
            onChange={setSupplierFilter}
            placeholderDeBusqueda="Buscar proveedor…"
            canDelete={false}
            opciones={suppliers.map((s) => ({ value: s, label: s }))}
          />
        </div>
        <div className="relative w-[150px] h-[38px] border border-[#E5E7EB] rounded-lg bg-white focus-within:ring-2 focus-within:ring-[#3B5998] focus-within:border-transparent">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm pointer-events-none select-none">
            {fromDate ? (
              <span className="text-gray-900 dark:text-white">
                {formatDateDDMMYYYY(fromDate)}
              </span>
            ) : (
              <span className="text-gray-400">dd/mm/aaaa</span>
            )}
          </span>
          <CalendarFolded className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-500 pointer-events-none" />
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            onClick={(e) =>
              (
                e.currentTarget as HTMLInputElement & {
                  showPicker?: () => void;
                }
              ).showPicker?.()
            }
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          />
        </div>
        <div className="relative w-[150px] h-[38px] border border-[#E5E7EB] rounded-lg bg-white focus-within:ring-2 focus-within:ring-[#3B5998] focus-within:border-transparent">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm pointer-events-none select-none">
            {toDate ? (
              <span className="text-gray-900 dark:text-white">
                {formatDateDDMMYYYY(toDate)}
              </span>
            ) : (
              <span className="text-gray-400">dd/mm/aaaa</span>
            )}
          </span>
          <CalendarFolded className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-500 pointer-events-none" />
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            onClick={(e) =>
              (
                e.currentTarget as HTMLInputElement & {
                  showPicker?: () => void;
                }
              ).showPicker?.()
            }
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          />
        </div>
        <button
          onClick={handleClearFilters}
          className="text-sm font-semibold text-[#315FB9] hover:underline"
        >
          Limpiar
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg overflow-hidden border border-[#E5E7EB]">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-[#E5E7EB]">
            <thead className="bg-[#FAF9FF]">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider whitespace-nowrap">
                  Proveedor
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider whitespace-nowrap">
                  RFC
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider whitespace-nowrap">
                  Folio factura
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider whitespace-nowrap">
                  Monto
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider whitespace-nowrap">
                  Estado
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider whitespace-nowrap">
                  Fecha de aprobación
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider whitespace-nowrap">
                  Fecha límite de pago
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider whitespace-nowrap">
                  Registrar pago
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-[#E1E1E1]">
              {loading ? (
                <tr>
                  <td colSpan={8} className="p-0">
                    <EsqueletoTabla filas={7} columnas={8} />
                  </td>
                </tr>
              ) : filteredRows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-16 text-center">
                    <img
                      src={emptyInvoice}
                      alt="Sin facturas"
                      className="w-24 h-24 mx-auto mb-4 object-contain"
                    />
                    <p className="text-base font-semibold text-gray-900 dark:text-white">
                      No se encontraron facturas
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      Intenta ajustar los filtros o el término de búsqueda
                    </p>
                  </td>
                </tr>
              ) : (
                filteredRows.map((row, idx) => {
                  const payment = row._payment;
                  const statusMeta = getPaymentStatusMeta(Boolean(payment));
                  return (
                    <tr
                      key={`${row.id}-${idx}`}
                      className="group hover:bg-[#F4F7FB] transition-colors"
                    >
                      <td className="px-6 py-4 text-sm min-w-[250px]">
                        {row.supplierName || "—----"}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {row.supplierRfc || "—----"}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {row.fiscal_folio || "—----"}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        $
                        {Number(
                          payment?.amount || row.invoice_total || 0,
                        ).toLocaleString("es-MX", { minimumFractionDigits: 2 })}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <Badge color={statusMeta.color}>{statusMeta.label}</Badge>
                      </td>

                      {/* Fecha de aprobación — usa la fecha de creación de la
                          factura de forma temporal (el back aún no captura una
                          fecha de aprobación propia). */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {row.created_at
                          ? purchaseOrdersRHService.formatDate(row.created_at)
                          : "—----"}
                      </td>

                      {/* Fecha límite de pago — fecha de vencimiento calculada al
                          aprobar la factura (fecha de carga + días de crédito). */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {row.due_date
                          ? purchaseOrdersRHService.formatDate(row.due_date)
                          : "—----"}
                      </td>

                      {/* Registrar pago — habilitado en facturas sin pago
                          (Pendiente de pago); las Pagadas quedan en gris (solo
                          lectura). */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {!payment ? (
                          <button
                            onClick={() => handleRegisterPago(row.id)}
                            className="inline-flex items-center gap-1.5 text-[#315FB9] hover:underline font-medium"
                          >
                            <FileUp className="w-4 h-4" />
                            Registrar pago
                          </button>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 text-gray-400 cursor-default select-none">
                            <FileUp className="w-4 h-4" />
                            Registrar pago
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Success notification */}
      {success && (
        <div className="fixed bottom-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-2 z-50">
          <CheckCircle className="w-5 h-5" />
          Pago agregado exitosamente
        </div>
      )}

      {/* Mensaje de descarga (sin datos / error) */}
      {downloadMsg && (
        <div className="fixed bottom-4 right-4 bg-[#D97706] text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-2 z-50">
          <Download className="w-5 h-5" />
          {downloadMsg}
        </div>
      )}

      {/* Add Payment Modal — mismo modal que PurchaseOrderPayments */}
      <AddPaymentModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSubmit={handleAddPayment}
        invoices={approvedInvoices}
        preselectedInvoiceId={preselectedInvoiceId}
      />
    </div>
  );
};

export default GlobalPaymentsView;
